import { useEffect, useState } from 'react';
import { api, ApiClientError } from '../../lib/api';
import { useToast } from '../../components/Toast';
import Modal from '../../components/Modal';
import {
  Banner, PageLoading, Select, TextArea, TextInput, Toggle,
} from '../../components/ui';
import type {
  Customer, CustomerFormApp, CustomerFormAppField, CustomerType, PossibleDuplicate,
} from '../../types';

/**
 * Adding a shop from the console.
 *
 * Reps add customers from the phone, where the form, the photo and the map
 * pin all come for free. The console path exists for the back-office cases —
 * seeding a new territory, fixing a miss — so it renders the same published
 * form, minus the two things a desk cannot capture: `photo` and `geo`.
 *
 * The one piece of real logic is the duplicate gate. The server decides a
 * shop looks like a repeat and answers 409; rather than guess, we show the
 * names it matched and let the admin — who can see the street — insist.
 */
export default function CustomerCreateDialog({
  onClose, onCreated,
}: { onClose: () => void; onCreated: () => void }) {
  const toast = useToast();

  const [form, setForm] = useState<CustomerFormApp | null>(null);
  const [types, setTypes] = useState<CustomerType[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [routeId, setRouteId] = useState('');
  const [typeId, setTypeId] = useState('');
  const [values, setValues] = useState<Record<string, unknown>>({});

  const [busy, setBusy] = useState(false);
  const [duplicates, setDuplicates] = useState<PossibleDuplicate[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [app, typeList] = await Promise.all([
          api.get<CustomerFormApp>('/customers/form/app'),
          api.get<CustomerType[]>('/customers/types'),
        ]);
        if (cancelled) return;
        setForm(app);
        setTypes(typeList);
      } catch (err) {
        if (!cancelled) {
          setLoadError(err instanceof ApiClientError ? err.message : 'Could not load the form.');
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // The two system fields live in their own controls above; everything a
  // desk cannot capture (a photo, a map pin) is dropped rather than shown.
  const customFields = (form?.fields ?? []).filter(
    (f) => !f.system && f.type !== 'photo' && f.type !== 'geo',
  );

  const setValue = (key: string, value: unknown) =>
    setValues((v) => ({ ...v, [key]: value }));

  function filled(field: CustomerFormAppField): boolean {
    const value = values[field.key];
    if (field.type === 'boolean') return true;
    if (field.type === 'multiselect') return Array.isArray(value) && value.length > 0;
    return value != null && String(value).trim() !== '';
  }

  const missingRequired = customFields.some((f) => f.required && !filled(f));
  const canSubmit = name.trim() !== '' && routeId !== '' && !missingRequired;

  async function submit(allowDuplicate: boolean) {
    if (!canSubmit) return;
    setBusy(true);
    try {
      await api.post<Customer>('/customers', {
        name: name.trim(),
        routeId,
        customerTypeId: typeId || undefined,
        values,
        allowDuplicate: allowDuplicate || undefined,
      });
      toast.success(`${name.trim()} added.`);
      onCreated();
    } catch (err) {
      if (err instanceof ApiClientError && err.code === 'POSSIBLE_DUPLICATE') {
        const matches = (err.details?.possibleDuplicates as PossibleDuplicate[]) ?? [];
        setDuplicates(matches);
      } else {
        toast.error(err instanceof ApiClientError ? err.message : 'Could not add that.');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title="Add a customer"
      subtitle="The same form your reps fill, minus the photo and map pin a phone captures."
      wide
      onClose={onClose}
      footer={(
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
          {duplicates ? (
            <button
              type="button"
              className="btn btn-primary"
              disabled={busy}
              onClick={() => void submit(true)}
            >
              {busy ? <span className="spinner" /> : null}
              Add anyway
            </button>
          ) : (
            <button
              type="button"
              className="btn btn-primary"
              disabled={busy || !canSubmit || !form}
              onClick={() => void submit(false)}
            >
              {busy ? <span className="spinner" /> : null}
              Add customer
            </button>
          )}
        </>
      )}
    >
      {loadError ? <Banner kind="danger">{loadError}</Banner> : null}
      {!form && !loadError ? <PageLoading rows={4} /> : null}

      {form ? (
        <div className="col gap-4">
          {duplicates ? (
            <Banner kind="warning">
              <strong>This looks like a shop that already exists.</strong>
              <div style={{ marginTop: 4 }}>
                {duplicates.length
                  ? <>Close to: {duplicates.map((d) => d.name).join(', ')}. </>
                  : null}
                If it really is a different shop, use <strong>Add anyway</strong>.
              </div>
            </Banner>
          ) : null}

          <TextInput
            label="Name *"
            value={name}
            placeholder="Shop or outlet name"
            onChange={(e) => { setName(e.target.value); setDuplicates(null); }}
          />

          <Select
            label="Area / Route *"
            value={routeId}
            onChange={(e) => setRouteId(e.target.value)}
          >
            <option value="">— Select an area —</option>
            {form.routes.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}{r.city ? ` · ${r.city}` : ''}
              </option>
            ))}
          </Select>

          <Select
            label="Type"
            value={typeId}
            hint="Sets the price list this shop buys on. Optional."
            onChange={(e) => setTypeId(e.target.value)}
          >
            <option value="">— None —</option>
            {types.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </Select>

          {customFields.map((field) => (
            <CustomFieldInput
              key={field.key}
              field={field}
              value={values[field.key]}
              onChange={(v) => setValue(field.key, v)}
            />
          ))}
        </div>
      ) : null}
    </Modal>
  );
}

/** Renders the one input a custom field's type asks for. */
function CustomFieldInput({
  field, value, onChange,
}: {
  field: CustomerFormAppField;
  value: unknown;
  onChange: (value: unknown) => void;
}) {
  const label = field.required ? `${field.label} *` : field.label;
  const hint = field.helpText;

  switch (field.type) {
    case 'textarea':
      return (
        <TextArea
          label={label}
          hint={hint}
          rows={3}
          placeholder={field.placeholder}
          value={(value as string) ?? ''}
          onChange={(e) => onChange(e.target.value)}
        />
      );

    case 'number':
      return (
        <TextInput
          label={label}
          hint={hint}
          type="number"
          placeholder={field.placeholder}
          value={(value as string) ?? ''}
          onChange={(e) => onChange(e.target.value)}
        />
      );

    case 'date':
      return (
        <TextInput
          label={label}
          hint={hint}
          type="date"
          value={(value as string) ?? ''}
          onChange={(e) => onChange(e.target.value)}
        />
      );

    case 'email':
      return (
        <TextInput
          label={label}
          hint={hint}
          type="email"
          placeholder={field.placeholder}
          value={(value as string) ?? ''}
          onChange={(e) => onChange(e.target.value)}
        />
      );

    case 'boolean':
      return (
        <div className="field">
          <Toggle
            checked={Boolean(value)}
            label={label}
            onChange={(checked) => onChange(checked)}
          />
          {hint ? <span className="field-hint">{hint}</span> : null}
        </div>
      );

    case 'select':
      return (
        <Select
          label={label}
          hint={hint}
          value={(value as string) ?? ''}
          onChange={(e) => onChange(e.target.value)}
        >
          <option value="">— Select —</option>
          {(field.options ?? []).map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </Select>
      );

    case 'multiselect': {
      const selected = Array.isArray(value) ? (value as string[]) : [];
      return (
        <div className="field">
          <label>{label}</label>
          <div className="col gap-4">
            {(field.options ?? []).map((o) => (
              <label key={o.value} className="row gap-8" style={{ alignItems: 'center' }}>
                <input
                  type="checkbox"
                  checked={selected.includes(o.value)}
                  onChange={(e) => onChange(
                    e.target.checked
                      ? [...selected, o.value]
                      : selected.filter((v) => v !== o.value),
                  )}
                />
                <span>{o.label}</span>
              </label>
            ))}
          </div>
          {hint ? <span className="field-hint">{hint}</span> : null}
        </div>
      );
    }

    // text, phone, gstin, pan — a plain text box, plus any type we do not
    // have a richer control for yet.
    default:
      return (
        <TextInput
          label={label}
          hint={hint}
          placeholder={field.placeholder}
          className={field.type === 'gstin' || field.type === 'pan' ? 'mono' : undefined}
          value={(value as string) ?? ''}
          onChange={(e) => onChange(e.target.value)}
        />
      );
  }
}
